/**
 * Utilidades para cálculo de similitud y diferencia de textos (Plan 1248)
 * Combinación de Similitud Jaccard (vocabulario/tokens) y Distancia Levenshtein normalizada
 */

export function levenshteinDistance(a, b) {
  const al = a.length;
  const bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;

  const matrix = [];
  for (let i = 0; i <= al; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= bl; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= al; i++) {
    for (let j = 1; j <= bl; j++) {
      if (a[i - 1] === b[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }
  return matrix[al][bl];
}

export function normalizeText(text) {
  if (!text) return "";
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remover tildes y diacríticos
    .replace(/[^\w\s]/g, " ")       // remover puntuación y caracteres especiales
    .replace(/\s+/g, " ")
    .trim();
}

export function getWordTokens(text) {
  const normalized = normalizeText(text);
  if (!normalized) return new Set();
  return new Set(normalized.split(" ").filter(w => w.length > 2));
}

export function calculatePairSimilarity(textA, textB) {
  if (!textA || !textB) return 0;
  const normA = normalizeText(textA);
  const normB = normalizeText(textB);

  if (normA === normB) return 1.0;
  if (!normA.length || !normB.length) return 0;

  // 1. Similitud Jaccard sobre palabras significativas
  const tokensA = getWordTokens(textA);
  const tokensB = getWordTokens(textB);

  let jaccard = 0;
  if (tokensA.size > 0 && tokensB.size > 0) {
    const intersection = new Set([...tokensA].filter(x => tokensB.has(x)));
    const union = new Set([...tokensA, ...tokensB]);
    jaccard = intersection.size / union.size;
  }

  // 2. Similitud Levenshtein normalizada (limitada a 500 chars para alta performance)
  const subA = normA.slice(0, 500);
  const subB = normB.slice(0, 500);
  const maxLen = Math.max(subA.length, subB.length);
  const dist = levenshteinDistance(subA, subB);
  const levSim = maxLen > 0 ? (1 - dist / maxLen) : 0;

  // 60% peso a vocabulario (Jaccard), 40% a estructura/secuencia (Levenshtein)
  return Math.max(0, Math.min(1, (0.6 * jaccard) + (0.4 * levSim)));
}

/**
 * Evalúa las 5 variaciones de mensajes para campañas directas
 * @param {Array<string>} messages Array de mensajes
 * @param {number} minLength Longitud mínima por mensaje (default: 20)
 * @param {number} maxAllowedSimilarity Máxima similitud permitida entre cualquier par (default: 0.70 -> 30% diferencia mínima)
 */
export function evaluate5MessagesVariation(messages, minLength = 20, maxAllowedSimilarity = 0.70) {
  const msgList = Array.isArray(messages) ? messages : [];
  const fiveMsgs = [
    (msgList[0] || "").trim(),
    (msgList[1] || "").trim(),
    (msgList[2] || "").trim(),
    (msgList[3] || "").trim(),
    (msgList[4] || "").trim()
  ];

  const statuses = fiveMsgs.map((msg, idx) => ({
    index: idx,
    length: msg.length,
    isLengthValid: msg.length >= minLength
  }));

  const filledCount = statuses.filter(s => s.isLengthValid).length;
  const isComplete = filledCount === 5;

  const pairComparisons = [];
  let maxSimilarity = 0;
  let totalDifference = 0;
  let pairCount = 0;

  for (let i = 0; i < 5; i++) {
    for (let j = i + 1; j < 5; j++) {
      const textA = fiveMsgs[i];
      const textB = fiveMsgs[j];
      let sim = 0;
      if (textA.length >= minLength && textB.length >= minLength) {
        sim = calculatePairSimilarity(textA, textB);
      }
      const diff = Math.round((1 - sim) * 100);
      const isConflict = sim > maxAllowedSimilarity;

      if (sim > maxSimilarity) {
        maxSimilarity = sim;
      }

      totalDifference += diff;
      pairCount++;

      pairComparisons.push({
        indexA: i,
        indexB: j,
        similarity: Math.round(sim * 100),
        difference: diff,
        isConflict
      });
    }
  }

  const conflicts = pairComparisons.filter(p => p.isConflict);
  const avgDifference = pairCount > 0 ? Math.round(totalDifference / pairCount) : 0;
  const minDifference = Math.round((1 - maxSimilarity) * 100);
  const isValid = isComplete && conflicts.length === 0;

  return {
    isComplete,
    isValid,
    filledCount,
    statuses,
    conflicts,
    maxSimilarity: Math.round(maxSimilarity * 100),
    minDifference,
    avgDifference,
    pairComparisons
  };
}
